import { useEffect, useState } from "react";
import { TextField } from "@mui/material";

import {
  parseCoordinateInput,
  stepCoordinate,
} from "@/features/widget-forms/forms/map-widget-editor/parse-coordinate-input";

/** Arrow-key increment; matches the former `type="number"` `step: 0.1`. */
const STEP = 0.1;

interface CoordinateFieldProps {
  className?: string;
  label: string;
  value: number;
  /** Arrow-key stepping is clamped to this range (lat ±90, lng ±180). */
  min: number;
  max: number;
  onCommit: (value: number) => void;
}

/**
 * Text-based coordinate input that works on mobile keyboards.
 *
 * Not `type="number"`: iOS/Android with an `nl` locale type `,` as decimal
 * separator, which a number input reports as `""` — the value never reaches the
 * form. Keeps a local draft so partial input (`-`, `52,`) survives re-renders;
 * re-syncs from `value` while not focused (drag handles, presets).
 */
export function CoordinateField({
  className,
  label,
  value,
  min,
  max,
  onCommit,
}: CoordinateFieldProps) {
  const [draft, setDraft] = useState(() => String(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(String(value));
  }, [value, focused]);

  return (
    <TextField
      className={className}
      label={label}
      type="text"
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value);
        const parsed = parseCoordinateInput(e.target.value);
        if (parsed !== undefined) onCommit(parsed);
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onKeyDown={(e) => {
        if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
        // Stop the caret jumping to start/end, which is the text-input default.
        e.preventDefault();
        // Step from what the user sees; fall back to the form value for partial drafts.
        const base = parseCoordinateInput(draft) ?? value;
        const next = stepCoordinate(base, e.key === "ArrowUp" ? STEP : -STEP, min, max);
        setDraft(String(next));
        onCommit(next);
      }}
      // Not `inputMode="decimal"`: iOS hides the minus key (needed for BES/west longitudes).
      inputProps={{ inputMode: "text", autoComplete: "off", spellCheck: false }}
      variant="standard"
      fullWidth
      sx={{ mt: 2 }}
    />
  );
}
